/**
 * Get the appropriate redirect path based on user role
 * @param {Object} profile - User profile object from useAuth
 * @returns {string} The redirect path
 */
export const getPropertyRedirectPath = (profile) => {
    // Check if user is ParkPro admin or super admin
    if (profile?.roles?.primary === 'PARKPRO_ADMIN' || profile?.roles?.primary === 'PARKPRO_SUPER_ADMIN') {
        return '/';
    }
    // For other roles (HOST_ADMIN, PROPERTY_MANAGER), redirect to My Properties
    return '/dashboard/my-properties';
};

/**
 * Whether `path` is safe to hand to react-router's navigate()/Navigate as a
 * post-login destination. Values sourced from a query string are
 * user-influenced (an attacker can craft a shared link with
 * `?redirect=https://evil.com`), so reject anything that isn't a same-origin
 * relative path before it's used.
 * @param {string} path
 * @returns {string|null}
 */
export const getSafeRedirectPath = (path) => {
    if (typeof path !== 'string' || !path || !path.startsWith('/')) {
        return null;
    }
    // Resolve against a fixed sentinel origin: if the path smuggles its own
    // scheme/authority (`//evil.com`, `/\evil.com` — browsers treat a leading
    // backslash like a slash for special schemes, so this also catches that
    // trick), the resolved origin won't match the sentinel. A plain path —
    // even one whose query string happens to contain "://" — resolves to the
    // same origin and is left untouched.
    const sentinelOrigin = 'https://__parkpro-redirect-check__.invalid';
    try {
        if (new URL(path, sentinelOrigin).origin !== sentinelOrigin) {
            return null;
        }
    } catch {
        return null;
    }
    return path;
};

/**
 * Builds the `/login?redirect=...` URL used by the session-expiry
 * interceptor (axios.js) to send a signed-out-mid-session user back to the
 * page they were on, including any query string and hash.
 * @param {string} pathname
 * @param {string} search
 * @param {string} hash
 * @returns {string}
 */
export const buildLoginRedirectUrl = (pathname, search = '', hash = '') =>
    `/login?redirect=${encodeURIComponent(`${pathname}${search}${hash}`)}`;